import React, { useState } from "react";
import {
	Box,
	Button
} from "@mui/material";
import {
	CheckCircle as PublishIcon,
	Cancel as RejectIcon,
	Undo as UnpublishIcon,
	Edit as EditIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../../theme";
import {
	ProjectEvaluation,
	ProjectEvaluationStatus
} from "../../../models/ProjectEvaluation";
import {
	useModerateProjectEvaluation,
	useEditProjectEvaluation
} from "../../../hooks/useProjectEvaluations";
import EvaluationEditDialog from "./EvaluationEditDialog";

interface EvaluationModerationPanelProps {
	evaluation: ProjectEvaluation;
}

const EvaluationModerationPanel: React.FC<EvaluationModerationPanelProps> = ({
	evaluation
}) => {
	const { t } = useTranslation();
	const moderateMutation = useModerateProjectEvaluation();
	const editMutation = useEditProjectEvaluation();

	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editRating, setEditRating] = useState<number>(evaluation.rating || 0);
	const [editComment, setEditComment] = useState<string>(evaluation.comment || "");

	const canPublish =
		evaluation.status === ProjectEvaluationStatus.Submitted ||
		evaluation.status === ProjectEvaluationStatus.Rejected;

	const canReject = evaluation.status === ProjectEvaluationStatus.Submitted;

	const canUnpublish = evaluation.status === ProjectEvaluationStatus.Published;

	const canEdit =
		evaluation.status === ProjectEvaluationStatus.Submitted ||
		evaluation.status === ProjectEvaluationStatus.Rejected;

	const handleModerate = (newStatus: ProjectEvaluationStatus) => {
		moderateMutation.mutate({
			id: evaluation.id,
			request: { newStatus }
		});
	};

	const handleOpenEdit = () => {
		setEditRating(evaluation.rating || 0);
		setEditComment(evaluation.comment || "");
		setEditDialogOpen(true);
	};

	const handleConfirmEdit = () => {
		editMutation.mutate(
			{
				id: evaluation.id,
				request: {
					rating: editRating,
					comment: editComment
				}
			},
			{ onSuccess: () => setEditDialogOpen(false) }
		);
	};

	return (
		<>
			<Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
				{canPublish && (
					<Button
						size="small"
						variant="outlined"
						startIcon={<PublishIcon />}
						disabled={moderateMutation.isPending}
						onClick={() => handleModerate(ProjectEvaluationStatus.Published)}
						sx={{
							color: zoullooColors.success.main,
							borderColor: zoullooColors.success.main,
							"&:hover": {
								borderColor: zoullooColors.success.dark,
								backgroundColor: zoullooColors.success.alpha.light
							}
						}}
					>
						{t("evaluations.actions.publish")}
					</Button>
				)}

				{canReject && (
					<Button
						size="small"
						variant="outlined"
						startIcon={<RejectIcon />}
						disabled={moderateMutation.isPending}
						onClick={() => handleModerate(ProjectEvaluationStatus.Rejected)}
						sx={{
							color: zoullooColors.error.main,
							borderColor: zoullooColors.error.main,
							"&:hover": {
								borderColor: zoullooColors.error.dark,
								backgroundColor: zoullooColors.error.alpha.light
							}
						}}
					>
						{t("evaluations.actions.reject")}
					</Button>
				)}

				{canUnpublish && (
					<Button
						size="small"
						variant="outlined"
						startIcon={<UnpublishIcon />}
						disabled={moderateMutation.isPending}
						onClick={() => handleModerate(ProjectEvaluationStatus.Submitted)}
						sx={{
							color: zoullooColors.warning.main,
							borderColor: zoullooColors.warning.main,
							"&:hover": {
								borderColor: zoullooColors.warning.dark,
								backgroundColor: zoullooColors.warning.light + "15"
							}
						}}
					>
						{t("evaluations.actions.unpublish")}
					</Button>
				)}

				{canEdit && (
					<Button
						size="small"
						variant="outlined"
						startIcon={<EditIcon />}
						onClick={handleOpenEdit}
						sx={{
							color: zoullooColors.secondary.main,
							borderColor: zoullooColors.secondary.main,
							"&:hover": {
								borderColor: zoullooColors.secondary.dark,
								backgroundColor: zoullooColors.secondary.alpha
							}
						}}
					>
						{t("evaluations.actions.edit")}
					</Button>
				)}
			</Box>

			{/* Edit Dialog */}
			<EvaluationEditDialog
				open={editDialogOpen}
				rating={editRating}
				comment={editComment}
				isPending={editMutation.isPending}
				onRatingChange={setEditRating}
				onCommentChange={setEditComment}
				onConfirm={handleConfirmEdit}
				onClose={() => setEditDialogOpen(false)}
			/>
		</>
	);
};

export default EvaluationModerationPanel;
